/* eslint-disable no-restricted-imports */
import { Button, Flex } from "@chakra-ui/react";
import { Form, Formik } from "formik";
import React from "react";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import {
  NavigationBlocker,
  useNavigationBlockerContext,
} from "src/components/NavigationBlocker";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import * as AF from "src/types/formTemplate";
import * as RD from "src/types/remoteData";
import { getNewVerificationId, isNewVerification } from "../../contents";
import { useEditFormTemplateContext } from "../../context";
import { ImportError } from "../ImportError";
import { LiveEditConfirmationProvider } from "../LiveEditConfirmation";
import { ValidationAlert } from "../ValidationAlert";
import { DirtyFormTracker } from "./DirtyFormTracker";
import { AnswersOptions } from "./Edit/AnswersOptions";
import { QuestionFormat } from "./Edit/QuestionContraints";
import { QuestionKey } from "./Edit/QuestionKey";
import { QuestionTitle } from "./Edit/QuestionTitle";
import { QuestionType } from "./Edit/QuestionType";
import {
  FormikType,
  toDeletedVerificationIds,
  toFormikValues,
  toQuestion,
  toVerification,
  validateForm,
} from "./formik";
import { ConfigureQuestion } from "./QuestionConfiguration/ConfigureQuestion";
import { SchoolItem } from "./types";

export type QuestionFormNewProps = {
  schools: RD.RemoteData<unknown, SchoolItem[]>;
  onClose: () => void;
  section: AF.V3.Section;
  otherQuestions: readonly AF.V3.Question[];
  isOpen: boolean;
  questionIndex: number;
};

export function QuestionFormNewDrawer({
  onClose,
  schools,
  section,
  otherQuestions,
  isOpen,
  questionIndex,
}: QuestionFormNewProps) {
  const { state, dispatch } = useEditFormTemplateContext();
  const verifications: AF.V3.FormVerification[] = React.useMemo(
    () => state.get("draft").verifications ?? [],
    [state]
  );

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const toast = useAvelaToast();
  const { grades } = useEditFormTemplateContext();

  const question: AF.V3.Question = React.useMemo(() => {
    return {
      id: window.crypto.randomUUID(),
      key: "",
      type: "FreeText",
      question: "",
    };
  }, []);

  const initialValues: FormikType = React.useMemo(
    () => toFormikValues(section.type, question, verifications),
    [question, verifications, section.type]
  );

  const onSubmit = React.useCallback(
    async (values: FormikType) => {
      setIsSubmitting(true);

      const updatedQuestion = toQuestion(
        { ...question, id: window.crypto.randomUUID() },
        values,
        grades
      );
      const updatedVerification = toVerification(values);
      const deletedVerificationIds = toDeletedVerificationIds(values);

      // if the verification is new, we need to generate a new id
      // and update the question with the new verification id
      if (updatedVerification && isNewVerification(updatedVerification)) {
        updatedVerification.id = getNewVerificationId();
        updatedQuestion.formVerificationId = updatedVerification.id;
      }

      try {
        dispatch({
          type: "AddQuestion",
          sectionId: section.id,
          question: updatedQuestion,
          atIndex: questionIndex,
          verification: updatedVerification,
          deletedVerificationIds,
        });

        onClose();
      } catch (err: unknown) {
        console.error(err);
        toast.genericError();
      } finally {
        setIsSubmitting(false);
        document.getElementById(updatedQuestion.id)?.scrollIntoView();
      }
    },
    [dispatch, grades, onClose, question, questionIndex, section.id, toast]
  );

  const { confirm, hasChanges } = useNavigationBlockerContext();
  const onCancelHandler = React.useCallback(async () => {
    if (!hasChanges || (await confirm())) {
      onClose();
    }
  }, [confirm, hasChanges, onClose]);

  return (
    <ResizableDrawer
      isOpen={isOpen}
      onClose={onCancelHandler}
      contentProps={{
        initialWidthPercent: 50,
      }}
    >
      <ValidationAlert id={question.id} />
      <ImportError id={question.id} />
      <Formik<FormikType>
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validateForm(section.type, state.get("draft"))}
      >
        {(formik) => {
          return (
            <LiveEditConfirmationProvider questionId={question.id}>
              <ResizableDrawer.Content as={Form} noValidate>
                <NavigationBlocker hasChanges={formik.dirty} />
                <ResizableDrawer.Header>Add question</ResizableDrawer.Header>
                <ResizableDrawer.Body>
                  <Flex direction="column" gap="6">
                    <DirtyFormTracker />
                    <QuestionTitle questionId={question.id} />
                    <QuestionKey
                      questionId={question.id}
                      otherQuestions={otherQuestions}
                    />
                    <QuestionType
                      questionId={question.id}
                      sectionType={section.type}
                    />
                    <QuestionFormat questionId={question.id} />
                    <AnswersOptions
                      question={question}
                      otherQuestions={otherQuestions}
                    />
                    <ConfigureQuestion
                      questionId={question.id}
                      sectionType={section.type}
                      otherQuestions={otherQuestions}
                      schools={schools}
                    />
                  </Flex>
                </ResizableDrawer.Body>
                <ResizableDrawer.Footer>
                  <Button
                    variant="outline"
                    colorScheme="gray"
                    onClick={onCancelHandler}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" isLoading={isSubmitting}>
                    Save question
                  </Button>
                </ResizableDrawer.Footer>
              </ResizableDrawer.Content>
            </LiveEditConfirmationProvider>
          );
        }}
      </Formik>
    </ResizableDrawer>
  );
}
